<template>
  <div class="service">
    <ul class="service-list-group">
      <li class="service-name">
        <h3>
          {{ item.name }}
          <b-badge v-if="hasIo" pill variant="success" class="ml-1">IO</b-badge>
          <b-badge v-if="hasWaf" pill variant="success" class="ml-1">WAF</b-badge>
          <b-badge v-if="hasBlockLog" pill variant="success" class="ml-1">
            Blocked Traffic Log
          </b-badge>
        </h3>
      </li>
      <li class="service-id"><span class="label">ID</span> {{ item.id }}</li>
      <li class="version-meta">
        <span class="label">Version</span> {{ item.version }}, {{ timeSince }}.
      </li>
      <li v-if="item.comment" class="service-id">
        <span class="label">Comments</span> {{ item.comment }}
      </li>
      <li v-if="'clients' in item" class="mb-1 d-flex">
        <div v-for="client in item.clients" :key="client.settings.prefix" class="mr-1">
          <b-button
            class="mr-1"
            @click="getDoc(client.settings.prefix, client.settings.docSnippetName)"
          >
            Show Documentation
            <span v-if="client.settings.prefix">[{{ client.settings.prefix }}]</span>
          </b-button>
          <b-button-group class="mr-1">
            <b-button @click="getStats(30)">30-day Stats</b-button>
            <b-button @click="getStats(60)">60-day Stats</b-button>
            <b-button @click="getStats(90)">90-day Stats</b-button>
          </b-button-group>
        </div>
      </li>
      <li v-if="'clients' in item" class="mb-1">
        <b-collapse
          v-for="client in item.clients"
          :id="`docs_${item.id}_${client.settings.prefix}_${client.settings.docSnippetName}`"
          :key="client.settings.prefix"
          v-model="expanded.docs"
        >
          <b-card>
            <b-card-text>
              <pre>{{
                cachedDocs[
                  `docs_${item.id}_${client.settings.prefix}_${client.settings.docSnippetName}`
                ]
              }}</pre>
            </b-card-text>
          </b-card>
        </b-collapse>

        <b-collapse
          v-for="period in [30, 60, 90]"
          :id="`stats_${item.id}_${period}`"
          :key="`stats_${item.id}_${period}`"
          v-model="expanded[`stats_${item.id}_${period}`]"
        >
          <StatsCard
            :stats="cachedStats[`stats_${item.id}_${period}`]"
            :service-id="item.id"
            :period="period"
            :has-io="hasIo"
            :has-waf="hasWaf"
            :has-block-log="hasBlockLog"
            @group-by-change="handleGroupByChange"
          />
        </b-collapse>
      </li>
      <li class="d-flex">
        <b-button class="mr-1" variant="success" @click="selectService(item)">
          Edit Dicts/ACLs
        </b-button>
        <b-dropdown class="mr-1" text="Purge Cache" variant="success">
          <template v-slot:button-content>
            Purge Cache
            <b-icon-arrow-repeat
              v-if="cacheOp.serviceId == item.id && cacheOp.running"
              class="animated"
            ></b-icon-arrow-repeat>
            <b-icon-alert-circle
              v-if="cacheOp.serviceId == item.id && cacheOp.error"
            ></b-icon-alert-circle>
            <b-icon-check-circle
              v-if="cacheOp.serviceId == item.id && cacheOp.success"
            ></b-icon-check-circle>
          </template>
          <b-dropdown-item-button @click="purge(item.id)">Purge All</b-dropdown-item-button>
          <!-- <b-dropdown-item href="#">Purge Domain</b-dropdown-item> -->
          <!-- <b-dropdown-item href="#">Purge URL</b-dropdown-item> -->
        </b-dropdown>
      </li>
    </ul>
  </div>
</template>

<style lang="scss">
ul {
  padding: 0;

  &.service-list-group {
    padding: 0;
    li {
      list-style: none;
      h3 {
        color: #6e818e;
        font-size: 1.2rem;
        margin-bottom: 5px;
      }
      .label {
        font-weight: bold;
      }
    }
  }
}

.b-icon.animated {
  animation-name: spinner;
  animation-duration: 1s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes spinner {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

<script>
/* eslint-disable */
import moment from 'moment';
import StatsCard from '@/components/StatsCard.vue';

export default {
  components: {
    StatsCard,
  },
  props: {
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      cachedDocs: {},
      cacheOp: {
        serviceId: '',
        running: false,
        error: false,
        success: false,
      },
      cachedStats: {},
      expanded: {
        docs: false,
        stats_30: false,
        stats_60: false,
        stats_90: false,
        waf_30: false,
        waf_60: false,
        waf_90: false,
      },
    };
  },
  methods: {
    async getDoc(prefix, docName) {
      if (this.expanded.docs === true) {
        this.expanded.docs = false;
      } else {
        if (!(`docs_${this.item.id}_${prefix}_${docName}` in this.cachedDocs)) {
          const finalDocName = (prefix !== '' ? `${prefix}_` : '') + docName;
          const docResp = await this.$api.fetch(`/service/${this.item.id}/version/${this.item.version}/vcls`);
          const toSet = docResp.data.filter(d => d.name === finalDocName)[0].content;
          this.$set(this.cachedDocs, `docs_${this.item.id}_${prefix}_${docName}`, toSet);
        }
        this.expanded.docs = true;
      }
    },
    async getStats(period) {
      if (this.expanded[`stats_${this.item.id}_${period}`] === true) {
        this.expanded[`stats_${this.item.id}_${period}`] = false;
      } else {
        // Go ahead and open the toggle now; don't wait for IO stats to populate.
        this.expanded[`stats_${this.item.id}_${period}`] = true;
        if (!(`stats_${this.item.id}_${period}` in this.cachedStats)) {
          const statsResp = await this.$api.fetch(`/stats/${this.item.id}/${period}`);
          const stats = statsResp.data;

          const reduceFields = [
            'requests',
            'bandwidth',
            'imgopto',
            'status_2xx',
            'status_3xx',
            'status_4xx',
            'status_5xx',
          ];
          const [
            requests,
            bandwidth,
            imgopto,
            status2xx,
            status3xx,
            status4xx,
            status5xx,
          ] = reduceFields.map(field =>
            stats.data.reduce((total, record) => total + record[field], 0)
          );
          this.$set(this.cachedStats, `stats_${this.item.id}_${period}`, {
            requests,
            bandwidth,
            imgopto,
            status2xx,
            status3xx,
            status4xx,
            status5xx,
            from: stats.meta.from,
            to: stats.meta.to,
          });

          this.$api.fetch(`/client_logs/ip/${this.item.id}/period/${period}`).then(ipStatsResp => {
            const ipStatsData = ipStatsResp.data;
            this.$set(
              this.cachedStats[`stats_${this.item.id}_${period}`],
              'ipstats_details',
              ipStatsData
            );
          });

          // Include IO stats if available. Don't do the whole "await" thing so we
          // can run the WAF data pull simultaneously.
          if (this.baseHasIo()) {
            // const ioStatsResp = await this.$api.fetch(`/io/${this.item.id}/period/${period}`);
            this.$api.fetch(`/io/${this.item.id}/period/${period}`).then(ioStatsResp => {
              const ioStatsData = ioStatsResp.data;
              this.$set(
                this.cachedStats[`stats_${this.item.id}_${period}`],
                'imgopto_details',
                ioStatsData
              );
            });
          }

          // Include WAF stats if available
          if (this.baseHasWaf()) {
            // const wafResp = await this.$api.fetch(`/waf/${this.item.id}/period/${period}`);
            this.$api.fetch(`/waf/${this.item.id}/period/${period}`).then(wafResp => {
              const summarized = {};
              wafResp.data.forEach(d => {
                if (!(d.host in summarized)) {
                  summarized[d.host] = {
                    pass_total: 0,
                    logged_total: 0,
                    blocked_total: 0
                  };
                }
                if (d.waf_logged === "0" && d.waf_blocked === "0") {
                  summarized[d.host].pass_total += parseInt(d.req_count);
                }
                else if (d.waf_logged === "1" && d.waf_blocked === "0") {
                  summarized[d.host].logged_total += parseInt(d.req_count);
                }
                else if (d.waf_blocked === "1") {
                  summarized[d.host].blocked_total += parseInt(d.req_count);
                }
              });
              this.$set(this.cachedStats[`stats_${this.item.id}_${period}`], 'waf_details', summarized);
            });
          }

          if (this.baseHasBlockLog()) {
            this.getBlockLog(period, this.baseDefaultBlockLogGrouping());
          }
        }
      }
    },
    async purge(serviceId) {
      this.cacheOp.serviceId = serviceId;
      this.cacheOp.error = false;
      this.cacheOp.success = false;
      this.cacheOp.running = true;
      const resp = await this.$api.patch(`/service/${serviceId}/purge`);
      this.cacheOp.running = false;
      if (resp.data.status !== 'ok') {
        this.cacheOp.error = true;
      } else {
        this.cacheOp.success = true;
      }
      return resp.data;
    },
    getBlockLog(period, aggregateType) {
      const _aggregateType = aggregateType
        ? aggregateType
        : this.baseDefaultBlockLogGrouping()

      this.$api.fetch(`/service/${this.item.id}/blocked_traffic/by/${_aggregateType}/period/${period}`).then(blockResp => {
        const blockedData = blockResp.data;
        this.$set(
          this.cachedStats[`stats_${this.item.id}_${period}`],
          'blocked_details',
          blockedData
        );
      });
    },
    baseHasIo() {
      if ('clients' in this.item && this.item.clients.length > 0) {
        return ('hasIo' in this.item.clients[0].settings);
      }
      return false;
    },
    baseHasWaf() {
      if ('clients' in this.item && this.item.clients.length > 0) {
        if ('hosts' in this.item.clients[0].settings) {
          const wafDomains = this.item.clients[0].settings.hosts.filter(d => /\.waf\.ps-pantheon\.com$/.test(d));
          return wafDomains.length > 0
        }
      }
      return false;
    },
    baseHasBlockLog() {
      if ('clients' in this.item && this.item.clients.length > 0) {
        return ('hasBlockLog' in this.item.clients[0].settings);
      }
      return false;
    },
    baseDefaultBlockLogGrouping() {
      if (this.baseHasBlockLog()) {
        return this.item.clients[0].settings.blockLogDefaultGroup;
      }
      return 'ip';
    },
    selectService(item) {
      this.$emit('active', { service: item.id });
      this.$emit('screen', 'tables');
    },
    handleGroupByChange(payload) {
      this.getBlockLog(payload.period, payload.newVal)
    },
  },
  computed: {
    hasIo() {
      return this.baseHasIo();
    },
    hasWaf() {
      return this.baseHasWaf();
    },
    hasBlockLog() {
      return this.baseHasBlockLog();
    },
    defaultBlockLogGrouping() {
      return this.baseDefaultBlockLogGrouping();
    },
    timeSince() {
      if (this.item.active_version_updated_at != 'n/a') {
        const ago = moment.duration(new moment().diff(this.item.active_version_updated_at)).humanize();
        return `activated about ${ago} ago (${this.item.active_version_updated_at})`;
      }
      return `not yet activated`;
    }
  },
};
</script>
